import { TimeSeriesExportInfo } from '@moderepo/mode-apis';
import { getLocalStorageItem, setLocalStorageItem, Time, TimeSeriesType } from '@moderepo/mode-app-components';
import moment from 'moment';
import { LS_KEY_DOWNLOAD_DATA_REQUESTS } from '../../utils/constants';

/**
 * A data structure that contain information about the downloadable data. This container is generic and does not care about what kind of
 * data the user is trying to download, sensor module data, robot data, mobility data, etc.... It only need to know the type of the data the user
 * is trying to download, simple time series data or collection, and the ID of the data.
 * 'name' is optional which can be used to display more user friend name and 'selected' is also optional which used for automatically
 * select the data.
 */
export interface DownloadableDataInfo {
    readonly type: TimeSeriesType;
    readonly id: string;
    readonly name?: string | undefined;
    readonly exportFileName?: string | undefined;
    readonly selected?: boolean | undefined;
}


export enum DownloadDataRequestStatus {
    PENDING = 'pending',        // Request sent but not completed. Data can't be downloaded yet.
    READY = 'ready',            // Request completed and data is ready to be downloaded.
    EXPIRED = 'expired',        // Request has expired and data can no longer be downloaded.
}


/**
 * This is the interface for the information related to a download data request we need to keep in localStorage. We will keep these in localStorage
 * so that the agent doesn't need to keep the browser open while they wait for the request to be ready. They can leave the app and come back
 * later to download the data when it is ready.
 */
export interface DownloadDataRequestInfo {
    readonly id: number;
    readonly projectId: number;
    readonly creationTime: string;
    readonly expirationTime: string;
    readonly status: DownloadDataRequestStatus;

    // The same information we need when we make a download data request except for the 'selected' field because we don't need to cache it
    readonly dataInfo: Omit<DownloadableDataInfo, 'selected'>[];
    readonly homeId: number;
    readonly tsdbSmartModuleId: string;

    readonly dataRange: {
        readonly start: string;
        readonly end: string;
    };
    readonly requestResponse: TimeSeriesExportInfo; // The response from the back end when the user made a request to download data
}


interface RequestsStore {
    [projectId: string]: DownloadDataRequestInfo[];
}


/**
 * This DownloadDataRequests manager manages all the download data request the agent made. It takes care of caching the request in localStorage
 * and updating the requests and status changed.
 */
export class TSDownloadDataRequestManager {

    private static instance: TSDownloadDataRequestManager = new TSDownloadDataRequestManager();

    public static getInstance (): TSDownloadDataRequestManager {
        return TSDownloadDataRequestManager.instance;
    }


    /**
     * Add a new request
     * @param projectId
     * @param dataInfo
     * @param dataRange
     * @param requestResponse
     */
    public addRequest (
        projectId: number, homeId: number, tsdbSmartModuleId: string, dataInfo: DownloadableDataInfo[],
        dataRange: {start: string, end: string}, requestResponse: TimeSeriesExportInfo,
    ) {
        const requestDate = moment();

        // The download link expires in 6 hours
        const expireTime = Time.HOUR_IN_MS * 6;

        // Create a new DownloadDataRequestInfo based on the provided info
        const newRequest: DownloadDataRequestInfo = {
            id            : Date.now(),
            creationTime  : requestDate.toISOString(),
            expirationTime: requestDate.add(expireTime, 'milliseconds').toISOString(),
            projectId,
            status        : DownloadDataRequestStatus.PENDING,
            dataInfo,
            homeId,
            tsdbSmartModuleId,
            dataRange,
            requestResponse,
        };

        // Get the latest request store from localStorage
        const requestsStore: RequestsStore = getLocalStorageItem(LS_KEY_DOWNLOAD_DATA_REQUESTS, {});

        // Check if we have a list of requests for the given projectId. If not, create one.
        if (!requestsStore[projectId]) {
            requestsStore[projectId] = [];
        }

        // Add the new request to the requestsStore and write the requestsStore to localStorage
        requestsStore[projectId].push(newRequest);
        setLocalStorageItem(LS_KEY_DOWNLOAD_DATA_REQUESTS, requestsStore);
    }


    /**
     * Remove a request by request id
     * @param projectId
     * @param id
     */
    public removeRequest (projectId: number, id: number) {
        const requestsStore: RequestsStore = getLocalStorageItem(LS_KEY_DOWNLOAD_DATA_REQUESTS, {});
        const index = requestsStore[projectId]?.findIndex((request: DownloadDataRequestInfo) => {
            return request.id === id;
        });

        if (index >= 0) {
            requestsStore[projectId].splice(index, 1);
            setLocalStorageItem(LS_KEY_DOWNLOAD_DATA_REQUESTS, requestsStore);
        }
    }


    /**
     * Change the request's status to READY
     * @param projectId
     * @param id
     */
    public markRequestReady (projectId, id: number) {
        const requestsStore: RequestsStore = getLocalStorageItem(LS_KEY_DOWNLOAD_DATA_REQUESTS, {});
        const index = requestsStore[projectId]?.findIndex((request: DownloadDataRequestInfo) => {
            return request.id === id;
        });

        if (index >= 0) {
            const requestsByProjectId: DownloadDataRequestInfo = requestsStore[projectId][index];
            const newRequest = {
                ...requestsByProjectId,
                status: DownloadDataRequestStatus.READY,
            };
            requestsStore[projectId][index] = newRequest;
            setLocalStorageItem(LS_KEY_DOWNLOAD_DATA_REQUESTS, requestsStore);
        }
    }


    /**
     * Change the request's status to EXPIRED
     * @param projectId
     * @param id
     */
    public markRequestExpired (projectId, id: number) {
        const requestsStore: RequestsStore = getLocalStorageItem(LS_KEY_DOWNLOAD_DATA_REQUESTS, {});
        const index = requestsStore[projectId]?.findIndex((request: DownloadDataRequestInfo) => {
            return request.id === id;
        });

        if (index >= 0) {
            const requestsByProjectId: DownloadDataRequestInfo = requestsStore[projectId][index];
            const newRequest = {
                ...requestsByProjectId,
                status: DownloadDataRequestStatus.EXPIRED,
            };
            requestsStore[projectId][index] = newRequest;
            setLocalStorageItem(LS_KEY_DOWNLOAD_DATA_REQUESTS, requestsStore);
        }
    }


    /**
     * Get all the requested for a given projectId
     * @param projectId
     */
    public getRequests (projectId: number): readonly DownloadDataRequestInfo[] {

        const requestsStore: RequestsStore = getLocalStorageItem(LS_KEY_DOWNLOAD_DATA_REQUESTS, {});
        const requestsByProjectId: DownloadDataRequestInfo[] = (requestsStore[projectId] || [])
            .filter((request: DownloadDataRequestInfo) => {
                // remove expired requests that are more than 2 days old
                return Date.parse(request.expirationTime).valueOf() > Date.now() - Time.DAY_IN_MS * 2;
            }).map((request: DownloadDataRequestInfo) => {
                if (Date.parse(request.expirationTime).valueOf() <= Date.now()) {
                    // this request has expired, mark it as expired and replaced the request at index with the modified request
                    return {
                        ...request,
                        status: DownloadDataRequestStatus.EXPIRED,
                    };
                }
                return request;
            });

        // re-write the modified requests back to localStorage to save the update
        requestsStore[projectId] = requestsByProjectId;
        setLocalStorageItem(LS_KEY_DOWNLOAD_DATA_REQUESTS, requestsStore);

        return requestsByProjectId;
    }
}
